<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  useId,
  useTemplateRef,
} from "vue";
import { toDomAttr, toMaybe, type Maybe } from "../../lib/maybe";
import { zwMenuKey, type ZwMenuActivateOn } from "./zwMenuContext";
import {
  useAnchoredFloating,
  type AnchorSource,
} from "../../composables/useAnchoredFloating";

const props = withDefaults(
  defineProps<{
    align?: "start" | "end";
    disabled?: boolean;
    /** Callers pass `just(text)` or `nothing` explicitly. */
    menuLabel: Maybe<string>;
    /** Prefer mousedown so editor selection isn't lost. */
    activateOn?: ZwMenuActivateOn;
  }>(),
  {
    align: "start",
    disabled: false,
    activateOn: "click",
  },
);

const open = defineModel<boolean>("open", { default: false });
const root = useTemplateRef<HTMLElement>("root");
const panel = useTemplateRef<HTMLElement>("panel");
const menuId = useId();

// The panel is teleported to <body> (so no `overflow: hidden` ancestor can clip
// it) and positioned against the trigger, flipping above it when there's no
// room below. Which is the common case for the format bubble on a phone.
const anchor = computed<AnchorSource>(() => toMaybe(root.value));
const panelMaybe = computed(() => toMaybe(panel.value));
const { floatingStyle, side } = useAnchoredFloating(anchor, panelMaybe, open, {
  placement: () => (props.align === "end" ? "bottom-end" : "bottom-start"),
  gap: 6,
});

const activateOn = computed(() => props.activateOn);

function setOpen(value: boolean): void {
  if (props.disabled && value) return;
  open.value = value;
}

function toggle(): void {
  setOpen(open.value === false);
}

function close(): void {
  open.value = false;
}

provide(zwMenuKey, {
  close,
  activateOn,
});

function onDocPointer(event: PointerEvent): void {
  if (open.value === false) return;
  const target = event.target;
  if (target instanceof Node === false) {
    close();
    return;
  }
  // The panel lives in <body>, not inside `root`. Check both, or the first
  // pointerdown on a menu item would close the menu before it could fire.
  const rootEl = root.value;
  const panelEl = panel.value;
  if (
    (rootEl !== null && rootEl.contains(target) === true) ||
    (panelEl !== null && panelEl.contains(target) === true)
  ) {
    return;
  }
  close();
}

function onKeydown(event: KeyboardEvent): void {
  if (open.value === false || event.key !== "Escape") return;
  event.preventDefault();
  // Capture + stop so parents (map deselect, shell dismiss) don't also run.
  event.stopPropagation();
  close();
}

onMounted(() => {
  window.addEventListener("pointerdown", onDocPointer, true);
  window.addEventListener("keydown", onKeydown, true);
});

onUnmounted(() => {
  window.removeEventListener("pointerdown", onDocPointer, true);
  window.removeEventListener("keydown", onKeydown, true);
});

const triggerAttrs = computed(() => ({
  "aria-expanded": open.value,
  "aria-haspopup": "menu" as const,
  "aria-controls": menuId,
  ...(props.disabled ? { disabled: true as const } : {}),
}));
</script>

<template>
  <div ref="root" class="zw-menu">
    <slot
      name="trigger"
      :open="open"
      :toggle="toggle"
      :close="close"
      :attrs="triggerAttrs"
    />
    <Teleport to="body">
      <div
        v-if="open"
        :id="menuId"
        ref="panel"
        class="zw-menu-panel"
        :data-side="side"
        role="menu"
        :aria-label="toDomAttr(menuLabel)"
        :style="floatingStyle"
      >
        <slot />
      </div>
    </Teleport>
  </div>
</template>

<style>
.zw-menu {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
}

/* Placement (position/left/top/max-height/max-width) is set inline by
   useAnchoredFloating, which flips and shrinks the panel to fit the viewport.
   `max-height` here is only a ceiling for the roomy case. The composable
   lowers it when space is tight. */
.zw-menu-panel {
  /* Above dialogs (100-110) and the divergence prompt (400): an open menu is
     always the frontmost transient surface, and only one can be open at once. */
  z-index: 500;
  min-width: 11.5rem;
  max-width: var(--zw-float-max-width, none);
  max-height: min(20rem, 50svh, var(--zw-float-max-height, 100svh));
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0.3rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
  /* NEVER ADD BOX SHADOW HERE. WE DON'T WANT IT. */
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  font-family: var(--font-ui);
}
</style>
